import React, { useEffect, useMemo, useRef, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { fmt } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, X, ShoppingCart, Pencil, Building2, Search, Printer, FileText, Truck, Calendar as CalendarIcon, ChevronDown, User, Phone, Mail, UserPlus } from "lucide-react";
import { printPurchase } from "@/lib/printPurchase";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const emptyForm = (defaultBranchId = "") => ({
  purchase_id: null,
  supplier_id: "",
  supplier: "",
  date: new Date().toISOString().slice(0, 10),
  notes: "",
  overall_discount: 0,
  branch_id: defaultBranchId,
  items: [{ product_id: "", qty: 1, unit_cost: 0, line_discount: 0 }],
});

export default function Purchases() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const { branches, activeBranchId } = useBranch();
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm(activeBranchId || ""));
  const [search, setSearch] = useState("");
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  // Searchable supplier dropdown state — open flag, typed query, and the inline
  // "new supplier" mini-form. Lives in this component so the parent form fields
  // (date, items, branch) survive the supplier creation flow.
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [supplierQuery, setSupplierQuery] = useState("");
  const [newSupplier, setNewSupplier] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [creatingNew, setCreatingNew] = useState(false);
  const supplierBoxRef = useRef(null);
  const supplierInputRef = useRef(null);

  const branchLabel = (b) => (lang === "ar" ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar)) || b.branch_id;
  const branchMap = useMemo(() => {
    const m = {};
    branches.forEach((b) => { m[b.branch_id] = branchLabel(b); });
    return m;
    // eslint-disable-next-line
  }, [branches, lang]);

  const load = async () => {
    const [p, pr, sp] = await Promise.all([
      api.get("/purchases"),
      api.get("/products"),
      api.get("/suppliers").catch(() => ({ data: [] })),
    ]);
    setPurchases(p.data); setProducts(pr.data); setSuppliers(sp.data || []);
  };
  useEffect(() => { load(); }, []);

  // Close supplier dropdown when clicking outside.
  useEffect(() => {
    if (!supplierOpen) return;
    const onDoc = (e) => {
      if (supplierBoxRef.current && !supplierBoxRef.current.contains(e.target)) {
        setSupplierOpen(false);
        setCreatingNew(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [supplierOpen]);

  const filteredSuppliers = useMemo(() => {
    const q = supplierQuery.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.phone || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q)
    );
  }, [suppliers, supplierQuery]);

  const pickSupplier = (s) => {
    setForm((f) => ({ ...f, supplier_id: s.supplier_id, supplier: s.name }));
    setSupplierOpen(false);
    setSupplierQuery("");
    setCreatingNew(false);
  };

  const openCreateSupplier = () => {
    // Pre-fill name with the current query so the user doesn't retype.
    setNewSupplier({ name: supplierQuery.trim(), phone: "", email: "", address: "", notes: "" });
    setCreatingNew(true);
  };

  const saveNewSupplier = async () => {
    if (!newSupplier.name.trim()) {
      return toast.error(lang === "ar" ? "اسم المورّد مطلوب" : "Supplier name is required");
    }
    setSavingSupplier(true);
    try {
      const r = await api.post("/suppliers", newSupplier);
      const created = r.data;
      // Refresh the cached list so the new supplier appears everywhere
      // (next time the modal opens, plus the Suppliers page list).
      setSuppliers((prev) => [created, ...prev]);
      pickSupplier(created);
      toast.success(lang === "ar" ? "تم إضافة المورّد" : "Supplier added");
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "تعذر الإضافة" : "Failed to add"));
    } finally {
      setSavingSupplier(false);
    }
  };

  const clearSupplier = () => {
    setForm((f) => ({ ...f, supplier_id: "", supplier: "" }));
    setSupplierQuery("");
  };

  // Invoice template (branding) is shared between POS invoices and Purchase
  // printouts so the user gets a consistent look from a single settings page.
  useEffect(() => {
    api.get("/invoice-template")
      .then((r) => setInvoiceTemplate(r.data || null))
      .catch(() => setInvoiceTemplate(null));
  }, []);

  // Filter purchases by purchase_number or supplier (case-insensitive).
  const filteredPurchases = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return purchases;
    return purchases.filter((p) =>
      (p.purchase_number || "").toLowerCase().includes(q) ||
      (p.supplier || "").toLowerCase().includes(q)
    );
  }, [purchases, search]);

  // KPIs — computed off the FULL list (not the filtered view).
  const kpis = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const month = today.slice(0, 7);
    const totalCount = purchases.length;
    const monthTotal = purchases
      .filter((p) => (p.date || "").startsWith(month))
      .reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
    const totalSpend = purchases.reduce((s, p) => s + (parseFloat(p.total) || 0), 0);
    const suppliers = new Set(purchases.map((p) => (p.supplier || "").trim()).filter(Boolean));
    return { totalCount, monthTotal, totalSpend, supplierCount: suppliers.size };
  }, [purchases]);

  const onPrint = (p) => {
    printPurchase(p, invoiceTemplate, branchMap[p.branch_id] || "", lang);
  };

  const subtotal = useMemo(
    () => form.items.reduce((s, i) => s + Math.max(0, (parseFloat(i.unit_cost) || 0) * (parseInt(i.qty) || 0) - (parseFloat(i.line_discount) || 0)), 0),
    [form.items]
  );
  const overallDisc = Math.min(subtotal, parseFloat(form.overall_discount) || 0);
  const total = subtotal - overallDisc;

  const addLine = () => setForm(f => ({ ...f, items: [...f.items, { _lid: `lid_${Date.now()}_${Math.random().toString(36).slice(2,8)}`, product_id: "", qty: 1, unit_cost: 0, line_discount: 0 }] }));
  const removeLine = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateLine = (i, field, val) => setForm(f => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, [field]: val } : x) }));

  const onSelectProduct = (i, productId) => {
    const prod = products.find(p => p.product_id === productId);
    setForm(f => ({
      ...f,
      items: f.items.map((x, idx) =>
        idx === i ? { ...x, product_id: productId, unit_cost: (!x.unit_cost || x.unit_cost == 0) && prod ? (prod.cost || 0) : x.unit_cost } : x
      ),
    }));
  };

  const submit = async () => {
    const valid = form.items.filter(i => i.product_id && parseInt(i.qty) > 0);
    if (valid.length === 0) { toast.error(t("required_field")); return; }
    if (branches.length > 0 && !form.branch_id && !form.purchase_id) {
      toast.error(lang === "ar" ? "اختر الفرع الذي ستُضاف إليه المشتريات" : "Select the branch that receives this purchase");
      return;
    }
    const payload = {
      supplier: form.supplier,
      supplier_id: form.supplier_id || undefined,
      date: form.date,
      notes: form.notes,
      overall_discount: parseFloat(form.overall_discount) || 0,
      items: valid.map(i => ({
        product_id: i.product_id,
        qty: parseInt(i.qty),
        unit_cost: parseFloat(i.unit_cost) || 0,
        line_discount: parseFloat(i.line_discount) || 0,
      })),
    };
    if (form.branch_id) payload.branch_id = form.branch_id;
    try {
      if (form.purchase_id) {
        await api.put(`/purchases/${form.purchase_id}`, payload);
      } else {
        await api.post("/purchases", payload);
      }
      toast.success(t("saved")); setOpen(false); setForm(emptyForm(activeBranchId || "")); load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    }
  };

  const editPurchase = (p) => {
    setForm({
      purchase_id: p.purchase_id,
      supplier_id: p.supplier_id || "",
      supplier: p.supplier || "",
      date: p.date,
      notes: p.notes || "",
      overall_discount: p.overall_discount || 0,
      branch_id: p.branch_id || "",
      items: p.items.map(it => ({
        product_id: it.product_id,
        qty: it.qty,
        unit_cost: it.unit_cost,
        line_discount: it.line_discount || 0,
      })),
    });
    setOpen(true);
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete?",
      description: "Stock will be reversed.",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/purchases/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="purchases-page">
      {/* ===== Hero header ===== */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-white to-rose-50 border border-[var(--border)] shadow-sm p-5">
        <div className="absolute -end-16 -top-16 w-44 h-44 rounded-full bg-gradient-to-br from-[var(--gold)] to-rose-500 opacity-[0.08]"/>
        <div className="absolute -start-10 -bottom-10 w-32 h-32 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 opacity-[0.07]"/>
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--gold)] to-rose-500 text-white flex items-center justify-center shadow-md shadow-rose-500/30">
              <Truck size={22}/>
            </div>
            <div>
              <h1 className="font-heading text-3xl gold-text leading-tight">{t("nav_purchases")}</h1>
              <p className="text-[11px] text-[var(--text-muted)] tracking-wider mt-0.5">{lang === "ar" ? "فواتير الشراء والمخزون الوارد" : "Purchase orders and inbound stock"}</p>
            </div>
          </div>
          <button onClick={() => { setForm(emptyForm(activeBranchId || "")); setOpen(true); }} data-testid="new-purchase-btn" className="btn-gold px-5 py-2.5 rounded-lg flex items-center gap-2 shadow-md">
            <Plus size={16}/>{t("new_purchase")}
          </button>
        </div>
      </div>

      {/* ===== KPIs ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile color="emerald" icon={FileText}
          label={lang === "ar" ? "إجمالي الفواتير" : "Total invoices"}
          value={kpis.totalCount} testId="kpi-total"/>
        <KpiTile color="gold" icon={CalendarIcon}
          label={lang === "ar" ? "إنفاق هذا الشهر" : "This month spend"}
          value={fmt(kpis.monthTotal)} testId="kpi-month"/>
        <KpiTile color="violet" icon={ShoppingCart}
          label={lang === "ar" ? "إجمالي الإنفاق" : "Total spend"}
          value={fmt(kpis.totalSpend)} testId="kpi-total-spend"/>
        <KpiTile color="rose" icon={Truck}
          label={lang === "ar" ? "عدد المورّدين" : "Suppliers"}
          value={kpis.supplierCount} testId="kpi-suppliers"/>
      </div>

      {/* ===== Search bar ===== */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={15} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)] pointer-events-none"/>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === "ar" ? "ابحث برقم الفاتورة أو المورّد..." : "Search by invoice # or supplier..."}
            className="input-dark ps-9 pe-8 py-2 w-full"
            data-testid="purchases-search"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="purchases-search-clear">
              <X size={14}/>
            </button>
          )}
        </div>
        <div className="text-xs text-[var(--text-muted)]">
          {search ? (
            <span data-testid="purchases-result-count">
              {lang === "ar" ? `${filteredPurchases.length} نتيجة من ${purchases.length}` : `${filteredPurchases.length} of ${purchases.length}`}
            </span>
          ) : (
            <span>{purchases.length} {lang === "ar" ? "فاتورة" : "invoices"}</span>
          )}
        </div>
      </div>

      {/* ===== Invoices table ===== */}
      <div className="card-surface overflow-auto">
        <table className="table-dark">
          <thead><tr>
            <th>{t("purchase_number")}</th><th>{lang === "ar" ? "الفرع" : "Branch"}</th><th>{t("supplier")}</th><th>{t("date")}</th><th>{t("items_count")}</th><th>{t("subtotal")}</th><th>{t("discount")}</th><th>{t("grand_total")}</th><th>{t("actions")}</th>
          </tr></thead>
          <tbody>
            {filteredPurchases.map(p => (
              <tr key={p.purchase_id} className="hover:bg-[var(--bg)] transition-colors" data-testid={`purchase-row-${p.purchase_id}`}>
                <td className="font-mono text-[var(--gold)] font-semibold">{p.purchase_number}</td>
                <td>
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--gold-tint)] text-[var(--gold)]">
                    <Building2 size={10} />{branchMap[p.branch_id] || "—"}
                  </span>
                </td>
                <td className="text-[var(--text)]">{p.supplier || "-"}</td>
                <td className="text-[var(--text-muted)] num-display">{p.date}</td>
                <td className="text-[var(--text-muted)] num-display text-center">{p.items?.length || 0}</td>
                <td className="text-[var(--text)] num-display">{fmt(p.subtotal || p.total)}</td>
                <td className="rose-text num-display">{p.overall_discount > 0 ? `−${fmt(p.overall_discount)}` : "—"}</td>
                <td className="gold-text font-bold num-display text-base">{fmt(p.total)}</td>
                <td>
                  <div className="flex gap-1.5">
                    <button onClick={() => onPrint(p)} data-testid={`print-purchase-${p.purchase_id}`} title={lang === "ar" ? "طباعة" : "Print"} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)] hover:bg-[var(--gold-tint)] text-[var(--gold)]"><Printer size={14}/></button>
                    <button onClick={() => editPurchase(p)} data-testid={`edit-purchase-${p.purchase_id}`} title={lang === "ar" ? "تعديل" : "Edit"} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)]"><Pencil size={14}/></button>
                    <button onClick={() => remove(p.purchase_id)} title={lang === "ar" ? "حذف" : "Delete"} className="p-1.5 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredPurchases.length === 0 && <tr><td colSpan={9} className="text-center text-[var(--text-muted)] py-12">{search ? (lang === "ar" ? "لا نتائج تطابق البحث" : "No results match your search") : t("no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-4xl space-y-4 max-h-[92vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text flex items-center gap-2">
              <ShoppingCart size={22}/>{form.purchase_id ? t("edit") : t("new_purchase")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Searchable supplier combobox + inline add */}
              <div className="relative" ref={supplierBoxRef}>
                <label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Truck size={12}/>{t("supplier")}</label>
                <button
                  type="button"
                  onClick={() => { setSupplierOpen((v) => !v); setCreatingNew(false); setTimeout(() => supplierInputRef.current?.focus(), 50); }}
                  data-testid="purchase-supplier-trigger"
                  className="input-dark w-full flex items-center justify-between text-start gap-1"
                >
                  <span className={form.supplier ? "text-[var(--text)] truncate" : "text-[var(--text-muted)]"}>
                    {form.supplier || (lang === "ar" ? "اختر مورّداً..." : "Choose a supplier...")}
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {form.supplier && (
                      <span onClick={(e) => { e.stopPropagation(); clearSupplier(); }} className="p-0.5 rounded hover:bg-[var(--bg)] text-[var(--text-muted)]">
                        <X size={12}/>
                      </span>
                    )}
                    <ChevronDown size={14} className={`transition-transform text-[var(--text-muted)] ${supplierOpen ? "rotate-180" : ""}`}/>
                  </div>
                </button>
                {supplierOpen && (
                  <div className="absolute z-30 mt-1 w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-xl overflow-hidden" data-testid="supplier-dropdown">
                    {!creatingNew ? (
                      <>
                        <div className="relative p-2 border-b border-[var(--border-subtle)]">
                          <Search size={12} className="absolute top-1/2 -translate-y-1/2 start-4 text-[var(--text-muted)] pointer-events-none"/>
                          <input
                            ref={supplierInputRef}
                            type="search"
                            value={supplierQuery}
                            onChange={(e) => setSupplierQuery(e.target.value)}
                            placeholder={lang === "ar" ? "ابحث بالاسم أو الهاتف..." : "Search by name or phone..."}
                            className="input-dark ps-7 py-1.5 w-full text-sm"
                            data-testid="supplier-search-input"
                          />
                        </div>
                        <div className="max-h-56 overflow-auto">
                          {filteredSuppliers.length === 0 && (
                            <div className="text-center text-xs text-[var(--text-muted)] py-6">
                              {supplierQuery
                                ? (lang === "ar" ? `لا يوجد مورّد بهذا الاسم` : "No supplier matches")
                                : (lang === "ar" ? "لا يوجد مورّدون بعد" : "No suppliers yet")}
                            </div>
                          )}
                          {filteredSuppliers.map((s) => (
                            <button
                              key={s.supplier_id}
                              type="button"
                              onClick={() => pickSupplier(s)}
                              data-testid={`supplier-option-${s.supplier_id}`}
                              className={`w-full text-start px-3 py-2 hover:bg-[var(--gold-tint)] border-b border-[var(--border-subtle)] last:border-b-0 ${form.supplier_id === s.supplier_id ? "bg-[var(--gold-tint)]" : ""}`}
                            >
                              <div className="text-sm text-[var(--text)] font-medium truncate">{s.name}</div>
                              {(s.phone || s.email) && (
                                <div className="text-[10px] text-[var(--text-muted)] truncate flex gap-2 mt-0.5">
                                  {s.phone && <span dir="ltr">{s.phone}</span>}
                                  {s.email && <span>{s.email}</span>}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={openCreateSupplier}
                          data-testid="supplier-add-new-btn"
                          className="w-full px-3 py-2 text-sm text-[var(--gold)] hover:bg-[var(--gold-tint)] border-t border-[var(--border-subtle)] flex items-center gap-1.5 font-medium"
                        >
                          <UserPlus size={13}/>
                          {supplierQuery
                            ? (lang === "ar" ? `إضافة "${supplierQuery}" كمورّد جديد` : `Add "${supplierQuery}" as new supplier`)
                            : (lang === "ar" ? "إضافة مورّد جديد" : "Add new supplier")}
                        </button>
                      </>
                    ) : (
                      <div className="p-3 space-y-2 bg-[var(--gold-tint)]" data-testid="new-supplier-panel">
                        <div className="text-xs font-semibold text-[var(--gold)] flex items-center gap-1 mb-1">
                          <UserPlus size={12}/>{lang === "ar" ? "إضافة مورّد جديد" : "New supplier"}
                        </div>
                        <div>
                          <label className="text-[10px] text-[var(--text-muted)] flex items-center gap-1"><User size={9}/>{lang === "ar" ? "الاسم *" : "Name *"}</label>
                          <input className="input-dark py-1.5 text-sm" value={newSupplier.name} onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })} data-testid="new-supplier-name" autoFocus/>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-[var(--text-muted)] flex items-center gap-1"><Phone size={9}/>{lang === "ar" ? "الهاتف" : "Phone"}</label>
                            <input className="input-dark py-1.5 text-sm" value={newSupplier.phone} onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })} data-testid="new-supplier-phone"/>
                          </div>
                          <div>
                            <label className="text-[10px] text-[var(--text-muted)] flex items-center gap-1"><Mail size={9}/>{lang === "ar" ? "البريد" : "Email"}</label>
                            <input className="input-dark py-1.5 text-sm" value={newSupplier.email} onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })} data-testid="new-supplier-email"/>
                          </div>
                        </div>
                        <div>
                          <label className="text-[10px] text-[var(--text-muted)]">{lang === "ar" ? "العنوان" : "Address"}</label>
                          <input className="input-dark py-1.5 text-sm" value={newSupplier.address} onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}/>
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                          <button type="button" onClick={() => setCreatingNew(false)} className="px-3 py-1 rounded border border-[var(--border)] text-xs">{t("cancel")}</button>
                          <button type="button" onClick={saveNewSupplier} disabled={savingSupplier} className="btn-gold px-3 py-1 rounded text-xs flex items-center gap-1" data-testid="save-new-supplier-btn">
                            <UserPlus size={11}/>{savingSupplier ? (lang === "ar" ? "..." : "...") : (lang === "ar" ? "حفظ" : "Save")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("date")}</label><input type="date" className="input-dark" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="purchase-date"/></div>
              {branches && branches.length > 0 ? (
                <div>
                  <label className="text-xs text-[var(--text-muted)] flex items-center gap-1"><Building2 size={12} />{lang === "ar" ? "الفرع المستلِم" : "Receiving branch"}</label>
                  <select className="input-dark" value={form.branch_id} onChange={e => setForm({...form, branch_id: e.target.value})} disabled={!!form.purchase_id} data-testid="purchase-branch">
                    <option value="">{lang === "ar" ? "اختر..." : "Select..."}</option>
                    {branches.map((b) => (
                      <option key={b.branch_id} value={b.branch_id}>{branchLabel(b)}{b.is_default ? " ★" : ""}</option>
                    ))}
                  </select>
                  {form.purchase_id && (
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{lang === "ar" ? "لا يمكن تغيير الفرع بعد الحفظ" : "Branch cannot be changed after save"}</div>
                  )}
                </div>
              ) : (
                <div><label className="text-xs text-[var(--text-muted)]">{t("notes")}</label><input className="input-dark" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/></div>
              )}
            </div>
            {branches && branches.length > 0 && (
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("notes")}</label>
                <input className="input-dark" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/>
              </div>
            )}

            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-xs text-[var(--text-muted)] uppercase tracking-wide px-2">
                <div className="col-span-4">{t("select_product")}</div>
                <div className="col-span-1">{t("qty")}</div>
                <div className="col-span-2">{t("unit_cost")}</div>
                <div className="col-span-2">{t("line_discount")}</div>
                <div className="col-span-2">{t("line_total")}</div>
                <div className="col-span-1"></div>
              </div>
              {form.items.map((it, i) => {
                const prod = products.find(p => p.product_id === it.product_id);
                const lineSub = Math.max(0, (parseFloat(it.unit_cost) || 0) * (parseInt(it.qty) || 0) - (parseFloat(it.line_discount) || 0));
                return (
                  <div key={it._lid || `row-${i}`} className="grid grid-cols-12 gap-2 items-center">
                    <select className="input-dark col-span-4" value={it.product_id} onChange={e => onSelectProduct(i, e.target.value)} data-testid={`purchase-product-${i}`}>
                      <option value="">---</option>
                      {products.map(p => <option key={p.product_id} value={p.product_id}>{lang === "ar" ? p.name_ar : p.name_en} {p.sku ? `(${p.sku})` : ""}</option>)}
                    </select>
                    <input type="number" min="1" className="input-dark col-span-1 num-display" value={it.qty} onChange={e => updateLine(i, "qty", e.target.value)} data-testid={`purchase-qty-${i}`}/>
                    <input type="number" step="0.01" min="0" className="input-dark col-span-2 num-display" value={it.unit_cost} onChange={e => updateLine(i, "unit_cost", e.target.value)} data-testid={`purchase-cost-${i}`}/>
                    <input type="number" step="0.01" min="0" className="input-dark col-span-2 num-display" value={it.line_discount} onChange={e => updateLine(i, "line_discount", e.target.value)} data-testid={`purchase-line-disc-${i}`}/>
                    <div className="col-span-2 text-sm font-medium gold-text text-center num-display">{fmt(lineSub)}</div>
                    <button onClick={() => removeLine(i)} className="col-span-1 p-2 rounded border border-[var(--border)] hover:border-red-500 flex items-center justify-center"><X size={14}/></button>
                    {prod && (
                      <div className="col-span-12 text-[10px] text-[var(--text-muted)] ps-2 num-display">
                        Current stock: {prod.stock} × {fmt(prod.cost)} ({t("avg_cost")})
                      </div>
                    )}
                  </div>
                );
              })}
              <button onClick={addLine} data-testid="purchase-add-line" className="btn-rose-outline px-4 py-2 rounded-md text-sm flex items-center gap-2"><Plus size={14}/>{t("add_item")}</button>
            </div>

            <div className="gold-divider"/>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div>
                <label className="text-xs text-[var(--text-muted)]">{t("overall_discount")}</label>
                <input type="number" step="0.01" min="0" className="input-dark num-display" value={form.overall_discount} onChange={e => setForm({...form, overall_discount: e.target.value})} data-testid="purchase-overall-discount"/>
                <div className="text-[10px] text-[var(--text-muted)] mt-1">Distributed proportionally across items</div>
              </div>
              <div className="space-y-1 text-end">
                <div className="flex justify-between text-sm"><span className="text-[var(--text-muted)]">{t("subtotal")}</span><span className="num-display">{fmt(subtotal)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-[var(--text-muted)]">{t("overall_discount")}</span><span className="rose-text num-display">−{fmt(overallDisc)}</span></div>
                <div className="flex justify-between font-heading text-2xl pt-2 border-t border-[var(--border-subtle)]"><span>{t("grand_total")}</span><span className="gold-text num-display">{fmt(total)}</span></div>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-[var(--text)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="purchase-save-btn" onClick={submit}>{form.purchase_id ? t("save_changes") : t("save_purchase")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, color = "gold", testId }) {
  const colorMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    gold: "bg-amber-50 border-amber-200 text-amber-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color] || colorMap.gold}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
        <Icon size={16} className="opacity-70"/>
      </div>
      <div className="font-heading text-2xl font-bold num-display mt-1.5">{value}</div>
    </div>
  );
}
